import { useCallback, useEffect, useMemo, useReducer } from 'react'

import { toast } from 'sonner'

import { getPursuitTypeFromOpportunityType } from '@/helpers/pursuit'

import { useSafePush } from '@/hooks/navigation/useSafePush'
import { useLatestOpportunityPursuit } from '@/hooks/opportunity/useLatestOpportunityPursuit'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useLatestPursuitSchemaId } from '@/hooks/pursuits/schemas/usePursuitSchemas'
import { useCreatePursuit } from '@/hooks/pursuits/useCreatePursuit'
import { useSelf } from '@/hooks/user/useSelf'

import { type UUID, isUUID } from '@/types/Id'
import type { OpportunityType } from '@/types/Opportunity'
import type { CreatePursuit, Pursuit } from '@/types/pursuit/Pursuit'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Unified input shape for opportunity data across all types.
 * Each display component transforms its data into this shape.
 */
export interface OpportunityInput {
  type: OpportunityType
  entityId: string
  title: string
  dueDate: Date | null
  description: string | null
}

/**
 * Options for the usePursuitCreation hook.
 */
export interface UsePursuitCreationOptions {
  /** The opportunity data to create a pursuit from */
  opportunity: OpportunityInput | null
  /** Optional search/campaign ID to associate with the pursuit */
  searchId?: string | null
  /** Whether the hook should be enabled (default: true) */
  enabled?: boolean
}

/**
 * State machine for pursuit creation flow.
 * Uses discriminated union for type-safe state handling.
 */
type PursuitState =
  | { status: 'idle' }
  | { status: 'creating' }
  | { status: 'created'; pursuitId: UUID }
  | { status: 'navigating' }

type PursuitAction =
  | { type: 'START_CREATE' }
  | { type: 'CREATE_SUCCESS'; pursuitId: UUID }
  | { type: 'CREATE_FAILURE' }
  | { type: 'START_NAVIGATE' }
  | { type: 'SET_EXISTING'; pursuitId: UUID }
  | { type: 'RESET' }

function pursuitReducer(
  state: PursuitState,
  action: PursuitAction,
): PursuitState {
  switch (action.type) {
    case 'START_CREATE':
      return { status: 'creating' }
    case 'CREATE_SUCCESS':
      return { status: 'created', pursuitId: action.pursuitId }
    case 'CREATE_FAILURE':
      return { status: 'idle' }
    case 'START_NAVIGATE':
      return { status: 'navigating' }
    case 'SET_EXISTING':
      return { status: 'created', pursuitId: action.pursuitId }
    case 'RESET':
      return { status: 'idle' }
    default:
      return state
  }
}

/**
 * Return type for the usePursuitCreation hook.
 * Provides state, derived UI state, and action handlers.
 */
export interface UsePursuitCreationReturn {
  // Current state
  state: PursuitState

  // Query state
  existingPursuit: Pursuit | null
  isLoadingExisting: boolean
  isFetchingExisting: boolean
  refetchExisting: () => void

  // Derived UI state (computed from state machine)
  canCreate: boolean
  canBeginResponse: boolean
  pursuitId: UUID | null
  pursuitUrl: string | null
  isCreating: boolean
  isNavigating: boolean
  hasCreatedPursuit: boolean

  // Button state helpers
  createButtonState: 'idle' | 'loading' | 'success'
  beginResponseButtonState: 'idle' | 'loading'

  // Actions
  createPursuit: () => Promise<void>
  beginResponse: () => Promise<void>
  viewPursuit: () => void
}

// ---------------------------------------------------------------------------
// Helper: Build CreatePursuit payload from OpportunityInput
// ---------------------------------------------------------------------------

function buildPursuitPayload(
  opportunity: OpportunityInput,
  userId: string | undefined,
  orgId: UUID,
  searchId: string | null | undefined,
  schemaId: UUID | null | undefined,
): CreatePursuit {
  const pursuitType = getPursuitTypeFromOpportunityType(opportunity.type)

  // Validate searchId is a UUID before passing it
  const validSearchId: UUID | null =
    searchId && isUUID(searchId) ? searchId : null

  const base: CreatePursuit = {
    title: opportunity.title || `${opportunity.type} pursuit`,
    user_ids: userId ? [userId] : [],
    due_date: opportunity.dueDate ?? new Date(),
    description: opportunity.description,
    search_id: validSearchId,
    estimated_value: 0,
    pwin: 0,
    pwin_override: null,
    type: pursuitType,
    organization_id: orgId,
    extended_schema_id: schemaId ?? null,
  }

  // Add the entity-specific pursuit field based on opportunity type
  switch (opportunity.type) {
    case 'federal_contract':
      return {
        ...base,
        federal_contract_pursuit: { entity_id: opportunity.entityId },
      }
    case 'federal_grant':
      return {
        ...base,
        federal_grant_pursuit: { entity_id: opportunity.entityId },
      }
    case 'sbir':
      return {
        ...base,
        sbir_pursuit: { entity_id: opportunity.entityId },
      }
    case 'sled_contract':
      return {
        ...base,
        sled_contract_pursuit: { entity_id: opportunity.entityId },
      }
    case 'dibbs_contract':
      return {
        ...base,
        dibbs_contract_pursuit: { entity_id: opportunity.entityId },
      }
    default: {
      const _exhaustive: never = opportunity.type
      throw new Error(`Unknown opportunity type: ${opportunity.type}`)
    }
  }
}

// ---------------------------------------------------------------------------
// Hook: usePursuitCreation
// ---------------------------------------------------------------------------

/**
 * Headless hook for pursuit creation flow.
 *
 * Manages the complete lifecycle of creating a pursuit from an opportunity:
 * - Checks for existing pursuits
 * - Builds the CreatePursuit payload from opportunity data
 * - Handles creation mutation with proper state transitions
 * - Provides navigation helpers for "Begin Response" flow
 *
 * @example
 * ```tsx
 * const {
 *   canCreate,
 *   createPursuit,
 *   beginResponse,
 *   createButtonState,
 * } = usePursuitCreation({
 *   opportunity: {
 *     type: 'federal_contract',
 *     entityId: notice.federal_contract_id,
 *     title: notice.title ?? 'Untitled',
 *     dueDate: notice.response_deadline ? new Date(notice.response_deadline) : null,
 *     description: contract.ai_summary,
 *   },
 *   searchId,
 * })
 * ```
 */
export function usePursuitCreation({
  opportunity,
  searchId,
  enabled = true,
}: UsePursuitCreationOptions): UsePursuitCreationReturn {
  const { data: user } = useSelf()
  const { activeOrgId } = useActiveOrgId()
  const { safePush } = useSafePush()

  const [state, dispatch] = useReducer(pursuitReducer, { status: 'idle' })

  // Fetch latest schema for extended data support
  const { data: latestSchemaId } = useLatestPursuitSchemaId({
    orgId: activeOrgId,
  })

  // Check for existing pursuit for this opportunity
  const {
    data: existingPursuit,
    isLoading: isLoadingExisting,
    isFetching: isFetchingExisting,
    refetch: refetchExisting,
  } = useLatestOpportunityPursuit({
    orgId: activeOrgId,
    opportunity_type: opportunity?.type ?? 'federal_contract',
    opportunity_id: opportunity?.entityId ?? '',
  })

  // Create pursuit mutation
  const { mutateAsync: createPursuitMutation } = useCreatePursuit({
    orgId: activeOrgId,
  })

  // Force refetch when opportunity changes to ensure fresh data
  useEffect(() => {
    if (opportunity?.entityId && isUUID(activeOrgId)) {
      refetchExisting()
    }
  }, [opportunity?.entityId, activeOrgId, refetchExisting])

  // ---------------------------------------------------------------------------
  // Derived UI State
  // ---------------------------------------------------------------------------

  // Derive pursuit ID from state (if just created) or existing pursuit from query
  const pursuitId =
    state.status === 'created' ? state.pursuitId : (existingPursuit?.id ?? null)

  const pursuitUrl = useMemo(() => {
    if (!isUUID(activeOrgId) || !pursuitId) return null
    return `/orgs/${activeOrgId}/pursuits/${pursuitId}`
  }, [activeOrgId, pursuitId])

  const isCreating = state.status === 'creating'
  const isNavigating = state.status === 'navigating'
  // Derive directly from query result - no state sync needed
  const hasCreatedPursuit =
    state.status === 'created' || existingPursuit !== null

  const canCreate = useMemo(() => {
    return (
      enabled &&
      opportunity !== null &&
      activeOrgId !== '' &&
      state.status === 'idle' &&
      !existingPursuit
    )
  }, [enabled, opportunity, activeOrgId, state.status, existingPursuit])

  const canBeginResponse = useMemo(() => {
    return (
      enabled &&
      opportunity !== null &&
      activeOrgId !== '' &&
      (state.status === 'idle' || state.status === 'created')
    )
  }, [enabled, opportunity, activeOrgId, state.status])

  // Button state helpers for easy UI binding
  const createButtonState = useMemo((): 'idle' | 'loading' | 'success' => {
    if (isCreating) return 'loading'
    if (hasCreatedPursuit) return 'success'
    return 'idle'
  }, [isCreating, hasCreatedPursuit])

  const beginResponseButtonState = useMemo((): 'idle' | 'loading' => {
    return isNavigating ? 'loading' : 'idle'
  }, [isNavigating])

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const createPursuit = useCallback(async () => {
    if (!opportunity || !isUUID(activeOrgId)) return
    if (state.status !== 'idle') return

    dispatch({ type: 'START_CREATE' })

    try {
      const payload = buildPursuitPayload(
        opportunity,
        user?.id,
        activeOrgId,
        searchId,
        latestSchemaId,
      )

      const created = await createPursuitMutation(payload)

      dispatch({ type: 'CREATE_SUCCESS', pursuitId: created.id })

      toast.success('Successfully created pursuit!', {
        action: {
          label: 'View',
          onClick: () => {
            safePush(`/orgs/${activeOrgId}/pursuits/${created.id}`)
          },
        },
      })
    } catch (error) {
      dispatch({ type: 'CREATE_FAILURE' })
      // Error handling is done by useCreatePursuit's onError
    }
  }, [
    opportunity,
    activeOrgId,
    state.status,
    user?.id,
    searchId,
    latestSchemaId,
    createPursuitMutation,
    safePush,
  ])

  const beginResponse = useCallback(async () => {
    if (!opportunity || !isUUID(activeOrgId)) return

    // If pursuit already exists, navigate directly with newDocument query param
    if (pursuitId) {
      safePush(
        `/orgs/${activeOrgId}/pursuits/${pursuitId}/documents?newDocument=true`,
      )
      return
    }

    // Create pursuit first, then navigate
    dispatch({ type: 'START_NAVIGATE' })

    try {
      const payload = buildPursuitPayload(
        opportunity,
        user?.id,
        activeOrgId,
        searchId,
        latestSchemaId,
      )

      const created = await createPursuitMutation(payload)

      dispatch({ type: 'CREATE_SUCCESS', pursuitId: created.id })
      // Navigate with newDocument query param to auto-open the dialog
      safePush(
        `/orgs/${activeOrgId}/pursuits/${created.id}/documents?newDocument=true`,
      )
    } catch (error) {
      dispatch({ type: 'CREATE_FAILURE' })
      // Error handling is done by useCreatePursuit's onError
    }
  }, [
    opportunity,
    activeOrgId,
    pursuitId,
    user?.id,
    searchId,
    latestSchemaId,
    createPursuitMutation,
    safePush,
  ])

  const viewPursuit = useCallback(() => {
    if (!activeOrgId || !pursuitId) return
    safePush(`/orgs/${activeOrgId}/pursuits/${pursuitId}`)
  }, [activeOrgId, pursuitId, safePush])

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    // State
    state,
    existingPursuit: existingPursuit ?? null,
    isLoadingExisting,
    isFetchingExisting,
    refetchExisting,

    // Derived UI state
    canCreate,
    canBeginResponse,
    pursuitId,
    pursuitUrl,
    isCreating,
    isNavigating,
    hasCreatedPursuit,

    // Button state helpers
    createButtonState,
    beginResponseButtonState,

    // Actions
    createPursuit,
    beginResponse,
    viewPursuit,
  }
}

// ---------------------------------------------------------------------------
// Helper hooks for building OpportunityInput from specific types
// ---------------------------------------------------------------------------

/**
 * Helper to build OpportunityInput from federal contract data.
 * Use in your component to transform contract-specific data to the unified shape.
 */
export function buildFederalContractInput(
  notice: {
    federal_contract_id: string
    title: string | null
    response_deadline?: string | Date | null
  },
  aiSummary?: string | null,
): OpportunityInput {
  return {
    type: 'federal_contract',
    entityId: notice.federal_contract_id,
    title:
      notice.title ?? `Federal contract pursuit ${notice.federal_contract_id}`,
    dueDate: notice.response_deadline
      ? new Date(notice.response_deadline)
      : null,
    description: aiSummary ?? null,
  }
}

/**
 * Helper to build OpportunityInput from federal grant data.
 */
export function buildFederalGrantInput(
  notice: {
    federal_grant_id: string
    opportunity_title: string | null
    close_date?: string | Date | null
  },
  aiSummary?: string | null,
): OpportunityInput {
  return {
    type: 'federal_grant',
    entityId: notice.federal_grant_id,
    title:
      notice.opportunity_title ??
      `Federal grant pursuit ${notice.federal_grant_id}`,
    dueDate: notice.close_date ? new Date(notice.close_date) : null,
    description: aiSummary ?? null,
  }
}

/**
 * Helper to build OpportunityInput from SBIR data.
 */
export function buildSbirInput(sbir: {
  id: string
  title: string | null
  application_due_date?: string | Date | null
  ai_summary?: string | null
}): OpportunityInput {
  return {
    type: 'sbir',
    entityId: sbir.id,
    title: sbir.title ?? `SBIR pursuit ${sbir.id}`,
    dueDate: sbir.application_due_date
      ? new Date(sbir.application_due_date)
      : null,
    description: sbir.ai_summary ?? null,
  }
}

/**
 * Helper to build OpportunityInput from SLED contract data.
 */
export function buildSledContractInput(
  notice: {
    sled_contract_id: string
    title: string | null
    response_deadline?: string | Date | null
  },
  aiSummary?: string | null,
): OpportunityInput {
  return {
    type: 'sled_contract',
    entityId: notice.sled_contract_id,
    title: notice.title ?? `SLED contract pursuit ${notice.sled_contract_id}`,
    dueDate: notice.response_deadline
      ? new Date(notice.response_deadline)
      : null,
    description: aiSummary ?? null,
  }
}
